import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField, Typography,
    Stack, Divider, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import * as api from './shippingMethodsApi';
import type { ShippingMethod, Language } from './shippingMethodsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell, mergeLocalizations } from '../common/detailView';

interface Props {
    shippingMethodId: number | null;
}

export function ShippingMethodView({ shippingMethodId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<ShippingMethod>();
    const [languages, setLanguages] = useState<Language[]>([]);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const langs = await api.getLanguages();
            setLanguages(langs);
            if (shippingMethodId === null) {
                detail.setItem({ id: 0, code: '', name: '', localizations: mergeLocalizations([], langs, buildDefaultLoc) });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getShippingMethod(shippingMethodId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [shippingMethodId]);

    useEffect(() => { load(); }, [load]);

    const isCodeValid = (): boolean => !!detail.item?.code && detail.item.code.length <= 20;

    const save = async () => {
        await detail.withSaving(async () => {
            const item = detail.item!;
            const response = detail.isNew ? await api.createShippingMethod(item) : await api.updateShippingMethod(item);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = async () => {
        if (!window.confirm(t('shippingmethod.confirmDelete'))) return;
        await detail.withSaving(async () => {
            await api.deleteShippingMethod(detail.item!.id);
            window.history.back();
        });
    };

    const updateLocalization = (languageId: number, name: string) => {
        if (!detail.item) return;
        detail.update('localizations', detail.item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    return (
        <DetailViewShell<ShippingMethod>
            title={detail.item ? `${detail.item.code} ${detail.item.name}` : ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isCodeValid()}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => {
                const sortedLocs = [...item.localizations].sort((a, b) =>
                    (a.language.name ?? '').localeCompare(b.language.name ?? '')
                );
                return (
                    <Stack spacing={2}>
                        <FormRow label={t('shippingmethod.code')}>
                            {!detail.isNew && !detail.isEditing ? (
                                <Typography sx={sx13}>{item.code}</Typography>
                            ) : (
                                <TextField size="small" fullWidth
                                    inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                    value={item.code}
                                    onChange={e => detail.update('code', e.target.value.toUpperCase())}
                                    error={!isCodeValid()} />
                            )}
                        </FormRow>

                        <FormRow label={t('shippingmethod.name')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx13}>{item.name}</Typography>
                            ) : (
                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                    value={item.name} onChange={e => detail.update('name', e.target.value)} />
                            )}
                        </FormRow>

                        <Divider />

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                    <TableCell sx={{ ...sx13, fontWeight: 'bold' }}>{t('shippingmethod.name')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLocs.map(loc => (
                                    <TableRow key={loc.language.id}>
                                        <TableCell sx={{ ...sx13, width: 150 }}>{loc.language.name}</TableCell>
                                        <TableCell>
                                            {!detail.isEditing ? (
                                                <Typography sx={sx13}>{loc.name}</Typography>
                                            ) : (
                                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                    value={loc.name ?? ''}
                                                    onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Stack>
                );
            }}
        </DetailViewShell>
    );
}
